import { useState, useEffect, useCallback } from "react";
import apiService from "../../../service/Api";
import { IStreamDetails } from "../../../types/streams";
import {
  CrewEntry,
  CrewEntryResponse,
} from "../components/callsheet/types/callsheet";
import { INetworkInfo } from "../types/view-stream";

export interface TimeAllocation {
  call_time?: string;
  departure_time?: string;
  arrival_at_venue_time?: string;
  setup_from_time?: string;
  setup_to_time?: string;
  shoot_start_time?: string;
  shoot_end_time?: string;
  estimated_wrap_time?: string;
  back_to_base_time?: string;
  unpacking_time?: string;
  depart_time?: string;
}

export const useStreamData = (id: string | undefined) => {
  const [stream, setStream] = useState<IStreamDetails | null>(null);
  const [crewMembersData, setCrewMembersData] = useState<CrewEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeData, setTimeData] = useState({
    call_time: "N/A",
    departure_time: "N/A",
    arrival_at_venue_time: "N/A",
    setup_from_time: "N/A",
    setup_to_time: "N/A",
    shoot_start_time: "N/A",
    shoot_end_time: "N/A",
    estimated_wrap_time: "N/A",
    back_to_base_time: "N/A",
    unpacking_time: "N/A",
    depart_time: "N/A",
  });
  const [networkData, setNetworkData] = useState<INetworkInfo[]>([]);

 const fetchSecondaryData = useCallback(
    async (streamId: string, fallback: { start?: string; end?: string }) => {
      apiService
        .get<{ results: INetworkInfo[] }>(
          `/api/streams/networking/?stream=${streamId}`,
        )
        .then((response) => setNetworkData(response.data.results))
        .catch((err) => console.error("Failed to fetch networking data", err));

      apiService
        .get<TimeAllocation>(`/api/streams/stream/${streamId}/time-algorithim/`)
        .then((timeResponse) => {
          const timeJson = timeResponse.data;
          setTimeData({
            call_time: timeJson.call_time || "N/A",
            departure_time: timeJson.departure_time || "N/A",
            arrival_at_venue_time: timeJson.arrival_at_venue_time || "N/A",
            setup_from_time: timeJson.setup_from_time || "N/A",
            setup_to_time: timeJson.setup_to_time || "N/A",
            shoot_start_time: timeJson.shoot_start_time || fallback.start || "N/A",
            shoot_end_time: timeJson.shoot_end_time || fallback.end || "N/A",
            estimated_wrap_time: timeJson.estimated_wrap_time || "N/A",
            back_to_base_time: timeJson.back_to_base_time || "N/A",
            unpacking_time: timeJson.unpacking_time || "N/A",
            depart_time: timeJson.depart_time || "N/A",
          });
        })
        .catch((err) => console.error("Time allocation fetch failed", err));

      apiService
        .get<CrewEntryResponse>(`/api/call-sheets/callsheet/?stream=${streamId}`)
        .then((res) => setCrewMembersData(res.data.results as CrewEntry[]))
        .catch((err) => console.error("Error fetching assigned crew members:", err));
    },
    [],
  );

  const fetchData = useCallback(async () => {
    if (!id) {
      setError("Invalid stream ID");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Core stream record -- render as soon as this lands, don't wait on
      // the secondary fetches below.
      const streamResponse = await apiService.get<IStreamDetails>(
        `/api/streams/stream/${id}/`
      );
      setStream(streamResponse.data);
      setIsLoading(false);

      fetchSecondaryData(id, {
        start: streamResponse.data.start,
        end: streamResponse.data.end,
      });
    } catch (err) {
      console.error("Stream details fetch failed", err);
      setError("Failed to load stream details.");
      setIsLoading(false);
    }
  }, [id, fetchSecondaryData]);

  const fetchNetworkData = async () => {
    if (!id) return;
    try {
      const response = await apiService.get<{ results: INetworkInfo[] }>(
        `/api/streams/networking/?stream=${id}`
      );
      setNetworkData(response.data.results);
    } catch (error) {
      console.error("Failed to fetch networking data", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    stream,
    crewMembersData,
    isLoading,
    error,
    timeData,
    networkData,
    fetchData,
    fetchNetworkData,
  };
};
